import React, { useEffect, useMemo, useState } from "react"
import { Box, Typography, Button, TextField, Pagination } from "@mui/material"
import { Container, Row, Col } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { blogsAPI } from "../../services/blogsAPI"
import RichTextRenderer from "../../components/RichTextRenderer"
import { getStrapiMedia } from "../../utils/strapiMedia"
import CallToAction from "../../components/CallToAction"

const ITEMS_PER_PAGE = 6

const Blogs = () => {
  const [blogs, setBlogs] = useState([])
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const data = await blogsAPI.getAllBlogs()
        setBlogs(data || [])
      } catch (err) {
        console.error("Error fetching blogs:", err)
      }
    }

    fetchBlogs()
  }, [])

  /* -------------------------------
     Search filter
  --------------------------------*/
  const filteredBlogs = useMemo(() => {
    return blogs.filter((blog) =>
      blog?.title?.toLowerCase().includes(search.toLowerCase()),
    )
  }, [blogs, search])

  /* -------------------------------
     Pagination logic
  --------------------------------*/
  const pageCount = Math.ceil(filteredBlogs.length / ITEMS_PER_PAGE)

  const paginatedBlogs = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE
    return filteredBlogs.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredBlogs, page])

  /* Reset page on search */
  useEffect(() => {
    setPage(1)
  }, [search])

  useEffect(() => {
    document.title = "Latest Blogs | First Legal India" // Change title here
  }, [])

  return (
    <>
      <Box sx={{ py: 6, bgcolor: "#f2f2f282", width: "100%" }}>
        <Container>
          <Typography variant='h4' mt={4} mb={1} align='center'>
            Our Blogs
          </Typography>

          <Typography variant='h6' mb={4} align='center'>
            Latest Blogs and Articles
          </Typography>

          {/* 🔍 Search */}
          <Box sx={{ maxWidth: 400, mx: "auto", mb: 4 }}>
            <TextField
              fullWidth
              placeholder='Search blogs...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>

          <Row className='g-4'>
            {paginatedBlogs.length === 0 && (
              <Typography align='center' sx={{ width: "100%", mt: 4 }}>
                No blogs found
              </Typography>
            )}

            {paginatedBlogs.map((blog) => (
              <Col xs={12} md={4} key={blog.id}>
                <Box
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {blog?.image && (
                    <Box
                      component='img'
                      src={getStrapiMedia(blog.image)}
                      alt={blog.title}
                      sx={{
                        width: "100%",
                        height: 200,
                        objectFit: "cover",
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      p: 3,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant='caption' color='text.secondary'>
                      {blog.date
                        ? new Date(blog.date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })
                        : ""}
                    </Typography>

                    <Typography variant='h6' mt={1}>
                      {blog.title}
                    </Typography>

                    <Box sx={{ mt: 1, mb: 2, flex: 1 }}>
                      <RichTextRenderer content={blog.summary} />
                    </Box>

                    <Button
                      variant='outlined'
                      size='small'
                      sx={{ mt: 2, alignSelf: "flex-start" }}
                      onClick={() => navigate(`/blogs/${blog.slug}`)}
                    >
                      Read More
                    </Button>
                  </Box>
                </Box>
              </Col>
            ))}
          </Row>

          {/* 📄 Pagination */}
          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(e, value) => setPage(value)}
                color='primary'
              />
            </Box>
          )}
        </Container>
      </Box>
      <CallToAction></CallToAction>
    </>
  )
}

export default Blogs
